/**
 * ReportProblemButton — in-app "Report a problem" (Problem Reporter, v0.3.2).
 *
 * Flow: click → html2canvas the CURRENT screen FIRST (so the modal isn't in the shot) →
 * downscale ≤1600px + JPEG-compress → open a modal with a thumbnail, a required
 * "What were you doing?" field, an optional "What did you expect?" field, and an
 * include-screenshot toggle (default on). On submit we assemble the diagnostic bundle and:
 *   • online  → POST /api/reports (success toast; on failure keep the modal + offer Retry)
 *   • offline → encrypt + enqueue to the separate report queue ("saved — will send on reconnect")
 *   • offline + no DEK (un-enrolled device) → button degrades to "reconnect to send" and does
 *     NOT queue (no plaintext PII at rest).
 *
 * Queued reports flush on the app's existing reconnect signal (subscribeConnection) + on mount.
 * Mounted in AppHeader so every authenticated page has it (login/reset pages don't use AppHeader).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { getConsoleTail } from '../lib/diagnostics/consoleBuffer';
import { getNetworkFails } from '../lib/diagnostics/networkBuffer';
import { listQueuedOps } from '../lib/offline/opQueue';
import { getDek, isUnlocked, subscribeDek } from '../lib/offline/dekSession';
import { subscribeConnection } from '../lib/connection';
import { enqueueReport, flushReports, type ReportPayload } from '../lib/offline/reportQueue';

const MAX_SHOT_PX = 1600;

type Toast = { kind: 'ok' | 'warn' | 'err'; msg: string } | null;

// Capture the current screen → downscaled JPEG data-URL (or null if capture fails / is blocked).
async function captureScreenshot(): Promise<string | null> {
  try {
    const source = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      backgroundColor: '#0f172a',
      scale: 1,
    });
    const scale = Math.min(1, MAX_SHOT_PX / Math.max(source.width, source.height));
    let canvas = source;
    if (scale < 1) {
      const scaled = document.createElement('canvas');
      scaled.width = Math.round(source.width * scale);
      scaled.height = Math.round(source.height * scale);
      const ctx = scaled.getContext('2d');
      if (ctx) {
        ctx.drawImage(source, 0, 0, scaled.width, scaled.height);
        canvas = scaled;
      }
    }
    return canvas.toDataURL('image/jpeg', 0.6); // toDataURL throws on a tainted (cross-origin) canvas
  } catch (e) {
    console.warn('[reports] screenshot capture failed', e);
    return null;
  }
}

type ReportProblemButtonProps = {
  /**
   * Mobile header variant (2026-07-23 header rework): render the trigger as a compact icon-only
   * button (life-ring glyph, 44px hit area) instead of the labelled text button. ONLY the trigger
   * presentation changes — capture, modal, toast and offline-queue behaviour are identical.
   */
  iconOnly?: boolean;
};

export default function ReportProblemButton({ iconOnly = false }: ReportProblemButtonProps) {
  const { user } = useAuth();

  // OS connectivity (drives the "reconnect to send" degradation) + DEK availability.
  const [online, setOnline] = useState<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [unlocked, setUnlocked] = useState<boolean>(isUnlocked());

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);          // capturing or submitting
  const [shot, setShot] = useState<string | null>(null); // data-URL
  const [includeShot, setIncludeShot] = useState(true);
  const [description, setDescription] = useState('');
  const [expected, setExpected] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // One id per REPORT (not per submit): a failed online submit + Retry re-POSTs the SAME
  // client_report_id, so the server's P2002-idempotent path returns the existing row instead
  // of duplicating. Reset in closeModal() so a brand-new report mints a fresh id.
  const reportIdRef = useRef<string | null>(null);

  // Track OS online/offline + DEK lock state.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const unsub = subscribeDek(() => setUnlocked(isUnlocked()));
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      unsub();
    };
  }, []);

  // Flush any queued reports on reconnect (the same signal OpsBoard drains the op-queue on) and
  // once on mount when already online. Idempotent + single-flight inside flushReports.
  useEffect(() => {
    if (!user) return;
    const tryFlush = () => { if (getDek()) void flushReports(user.id); };
    if (navigator.onLine) tryFlush();
    const unsub = subscribeConnection((isOnline) => { if (isOnline) tryFlush(); });
    return unsub;
  }, [user]);

  const showToast = useCallback((t: Toast) => {
    setToast(t);
    if (t) window.setTimeout(() => setToast(null), 4500);
  }, []);

  const degraded = !online && !unlocked; // offline + un-enrolled → cannot queue safely

  const closeModal = useCallback(() => {
    setOpen(false);
    setShot(null);
    setDescription('');
    setExpected('');
    setErr(null);
    setBusy(false);
    reportIdRef.current = null; // next distinct report gets a fresh idempotency id
    triggerRef.current?.focus();
  }, []);

  const onTriggerClick = useCallback(async () => {
    if (degraded) {
      showToast({ kind: 'warn', msg: 'Reconnect to send a report (this device is offline and not set up for offline use).' });
      return;
    }
    reportIdRef.current = crypto.randomUUID(); // mint the report's idempotency id once
    setBusy(true);
    const captured = await captureScreenshot(); // BEFORE opening the modal
    setShot(captured);
    setIncludeShot(!!captured);
    setBusy(false);
    setOpen(true);
  }, [degraded, showToast]);

  // Focus the first field when the modal opens.
  useEffect(() => {
    if (open) window.setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [open]);

  const buildPayload = useCallback(async (): Promise<ReportPayload> => {
    let queueDepth = 0;
    try {
      if (user) queueDepth = (await listQueuedOps(user.id)).length;
    } catch { queueDepth = 0; }

    const includeImage = includeShot && !!shot;
    const base64 = includeImage && shot ? shot.split(',')[1] : undefined;

    // Reuse the report's id across Retries (lazily mint if somehow unset).
    if (!reportIdRef.current) reportIdRef.current = crypto.randomUUID();

    return {
      client_report_id: reportIdRef.current,
      client_created_at: new Date().toISOString(),
      route: window.location.pathname + window.location.search,
      app_version: __APP_VERSION__,
      user_agent: navigator.userAgent,
      online: navigator.onLine,
      queue_depth: queueDepth,
      console_tail: getConsoleTail(),
      network_fails: getNetworkFails(),
      description: description.trim(),
      ...(expected.trim() ? { expected: expected.trim() } : {}),
      ...(includeImage && base64 ? { screenshot: base64, screenshot_mime: 'image/jpeg' as const } : {}),
    };
  }, [user, includeShot, shot, description, expected]);

  const submit = useCallback(async () => {
    setErr(null);
    if (!description.trim()) {
      setErr('Please tell us what you were doing.');
      firstFieldRef.current?.focus();
      return;
    }
    setBusy(true);
    const payload = await buildPayload();

    if (navigator.onLine) {
      try {
        const res = await apiFetch<{ id: string; ticket?: string; ticket_no?: number }>('/api/reports', { method: 'POST', body: JSON.stringify(payload) });
        if (res.error || !res.data?.id) {
          setBusy(false);
          setErr(res.error?.message ?? 'Could not send your report. Please retry.');
          return; // keep the modal open → the Send button doubles as Retry
        }
        showToast({
          kind: 'ok',
          msg: res.data.ticket ? `Thanks — your report was logged as ${res.data.ticket}.` : 'Thanks — your report was sent.',
        });
        closeModal();
      } catch {
        // Network dropped mid-send: keep the modal open and offer Retry.
        setBusy(false);
        setErr('No connection — please retry when you’re back online.');
      }
      return;
    }

    // Offline: encrypt + enqueue (DEK guaranteed here — degraded state blocks the button).
    const queued = await enqueueReport(payload);
    setBusy(false);
    if (queued) {
      showToast({ kind: 'ok', msg: 'Saved — your report will send when you reconnect.' });
      closeModal();
    } else {
      setErr('Could not save the report on this device. Reconnect to send.');
    }
  }, [description, buildPayload, showToast, closeModal]);

  // Esc to close + basic focus trap on Tab.
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); closeModal(); return; }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [closeModal]);

  return (
    <>
      {iconOnly ? (
        // Mobile trigger: a 44×44 hit area (transparent) drawing a compact 34×34 visual box, so
        // the touch target meets the 44px minimum while the bar stays dense. Same handler/state.
        <button
          ref={triggerRef}
          type="button"
          onClick={onTriggerClick}
          disabled={busy}
          aria-haspopup="dialog"
          aria-label="Report a problem"
          title={degraded ? 'Reconnect to send a report' : 'Report a problem'}
          style={{
            width: 44, height: 44, padding: 0, background: 'transparent', border: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: busy ? 'wait' : 'pointer', flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
              borderRadius: 8, color: degraded ? '#64748b' : 'rgba(255,255,255,0.9)', fontSize: 15, lineHeight: 1,
            }}
          >
            {degraded ? '⚠' : busy && !open ? '📷' : '🛟'}
          </span>
        </button>
      ) : (
      <button
        ref={triggerRef}
        type="button"
        onClick={onTriggerClick}
        disabled={busy}
        aria-haspopup="dialog"
        title={degraded ? 'Reconnect to send a report' : 'Report a problem'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', color: degraded ? '#64748b' : '#cbd5e1',
          border: '1px solid #334155', borderRadius: 6, padding: '4px 10px',
          fontSize: 12.5, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {degraded ? '⚠ Reconnect to send' : busy && !open ? '📷 Capturing…' : '🛟 Report a problem'}
      </button>
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, maxWidth: 460, padding: '10px 16px', borderRadius: 8, fontSize: 13,
            color: '#f8fafc', boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
            background: toast.kind === 'ok' ? '#15803d' : toast.kind === 'warn' ? '#b45309' : '#b91c1c',
          }}
        >
          {toast.msg}
        </div>
      )}

      {open && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          onKeyDown={onKeyDown}
          style={{
            position: 'fixed', inset: 0, zIndex: 1001, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 16,
            background: 'rgba(2,6,23,0.6)',
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
            style={{
              width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
              background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
              padding: 20, color: '#e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 id="report-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Report a problem</h2>
              <button
                type="button" onClick={closeModal} aria-label="Close"
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>

            <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#94a3b8' }}>
              We’ll automatically include technical details (page, app version, recent errors) to help us fix it.
              {!online && ' You’re offline — this will be saved and sent when you reconnect.'}
            </p>

            {shot && (
              <div style={{ marginBottom: 14 }}>
                <img
                  src={shot} alt="Screenshot preview"
                  style={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 8, border: '1px solid #334155', background: '#020617' }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12.5 }}>
                  <input type="checkbox" checked={includeShot} onChange={(e) => setIncludeShot(e.target.checked)} />
                  Include this screenshot
                </label>
              </div>
            )}

            <label htmlFor="report-desc" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
              What were you doing? <span style={{ color: '#f87171' }}>*</span>
            </label>
            <textarea
              id="report-desc" ref={firstFieldRef} value={description}
              onChange={(e) => setDescription(e.target.value)} maxLength={4000} rows={3}
              placeholder="e.g. Saving a booking for VH-ABC and it spun forever."
              style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical', marginBottom: 12 }}
            />

            <label htmlFor="report-expected" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
              What did you expect to happen? <span style={{ color: '#64748b', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              id="report-expected" value={expected}
              onChange={(e) => setExpected(e.target.value)} maxLength={4000} rows={2}
              placeholder="e.g. It should have saved and shown the booking on the board."
              style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical', marginBottom: 12 }}
            />

            {err && <div role="alert" style={{ color: '#fca5a5', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button" onClick={closeModal} disabled={busy}
                style={{ background: 'transparent', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}
              >Cancel</button>
              <button
                type="button" onClick={submit} disabled={busy}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}
              >{busy ? 'Sending…' : err ? 'Retry' : online ? 'Send report' : 'Save report'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
